import i18n from 'common/util/internationalization'
import { getPostHeaders } from 'common/util/requestUtils'
import emitter from 'common/util/events'
import { removeSessionStorageItem } from 'businesslayer/minutesLocalStore'
import type { ValidationApiResponse } from 'pages/writing-styles/writingStyleValidationTypes'

/**
 * Validates response status and handles common error cases (401, !response.ok)
 * Returns the response if valid, otherwise rejects the promise
 */
const validateResponse = (response: Response): Response => {
    if (response.status === 401) {
        const message = i18n.t('SESSION_EXPIRED_MESSAGE')
        console.error(message)
        removeSessionStorageItem('selectedStatusFilter')
        removeSessionStorageItem('selectedCommitteeFilter')
        emitter.emit('locked', { message })
        return Promise.reject(response) as any
    }
    if (!response.ok) {
        return Promise.reject(response) as any
    }
    return response
}

/**
 * Parses response that may be empty (204 No Content) or JSON
 * Returns null for empty responses, parsed JSON for content, or null if no JSON content
 */
const parseResponseOrNull = async (response: Response): Promise<any> => {
    // Handle empty response (204 No Content)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
        return null
    }
    // Check if response has JSON content
    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
        return response.json()
    }
    return null
}

export const generateWritingStyles = async (committeeIds: number[], id?: string) => {
    try {
        const body = JSON.stringify({
            ...(id && { id }),
            committee_ids: committeeIds
        })

        return fetch(`/api/writing_styles/generate`, {
            method: 'POST',
            credentials: 'include',
            headers: getPostHeaders(),
            body
        }).then((response) => {
            return validateResponse(response)
        }).then((response) => response.json())
    } catch (error) {
        throw new Error(i18n.t('WRITING_STYLE_GENERATION_ERROR'))
    }
}

export const acknowledgeWritingStyle = async (writingStyleId: string | number, data: any) => {
    try {
        const body = JSON.stringify(data)

        return fetch(`/api/writing_styles/${writingStyleId}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: getPostHeaders(),
            body
        }).then((response) => {
            return validateResponse(response)
        }).then((response) => response.json())
    } catch (error) {
        throw new Error(i18n.t('WRITING_STYLE_GENERATION_ERROR'))
    }
}
export const getWritingStyle = async () => {
    try {
        return fetch(`/api/writing_styles`, {
            method: 'GET',
            credentials: 'include',
            headers: getPostHeaders()
        }).then((response) => {
            return validateResponse(response)
        }).then((response) => response.json())
    } catch (error) {
        console.error('Error generating writing style. Please try again.', error)
    }
}

export const getAIStyleById = async (id: string | number) => {
    try {
        return fetch(`/api/writing_styles/${id}`, {
            method: 'GET',
            credentials: 'include',
            headers: getPostHeaders()
        }).then((response) => {
            return validateResponse(response)
        }).then((response) => response.json())
    } catch (error) {
        console.error('Error fetching writing style. Please try again.', error)
        throw error
    }
}

export const updateWritingStylePreference = async (id: string, preferred: boolean) => {
    try {
        return fetch(`/api/writing_styles/${id}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: getPostHeaders(),
            body: JSON.stringify({
                data: {
                    attributes: {
                        preferred
                    }
                }
            })
        }).then((response) => {
            return validateResponse(response)
        }).then((response) => response.json())
    } catch (error) {
        console.error('Error generating writing style. Please try again.', error)
    }
}

export const updateWritingStyleAIMinutes = async (writingStyleId: string | number) => {
    try {
        const body = JSON.stringify({
            data: {
                attributes: {
                    view_toast: false
                }
            }
        })

        return fetch(`/api/writing_styles/${writingStyleId}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: getPostHeaders(),
            body
        }).then((response) => {
            return validateResponse(response)
        }).then((response) => parseResponseOrNull(response))
    } catch (error) {
        console.error('Error generating writing style. Please try again.', error)
    }
}

type SavePromptResult =
    | { ok: true; data: any }
    | { ok: false; validation: ValidationApiResponse }

export const saveWritingStylePrompt = async (
    writingStyleId: string | number,
    generatedStyle: string
): Promise<SavePromptResult> => {
    const response = await fetch(`/api/writing_styles/${writingStyleId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: getPostHeaders(),
        body: JSON.stringify({ data: { attributes: { generated_style: generatedStyle } } })
    })

    if (!response.ok) {
        if (response.status === 401) {
            const message = i18n.t('SESSION_EXPIRED_MESSAGE')
            removeSessionStorageItem('selectedStatusFilter')
            removeSessionStorageItem('selectedCommitteeFilter')
            emitter.emit('locked', { message })
            throw new Error(response.statusText)
        }
        try {
            const body: ValidationApiResponse = await response.json()
            return { ok: false, validation: body }
        } catch {
            throw new Error(response.statusText)
        }
    }

    return { ok: true, data: await parseResponseOrNull(response) }
}

export const deleteCustomAIStyle = async (writingStyleId: string | number) => {
    try {
        return fetch(`/api/writing_styles/${writingStyleId}`, {
            method: 'DELETE',
            credentials: 'include',
            headers: getPostHeaders()
        }).then((response) => {
            return validateResponse(response)
        }).then((response) => parseResponseOrNull(response))
    } catch (error) {
        console.error('Error generating writing style. Please try again.', error)
    }
}
